import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { TravelerRefSchema } from '../../shared/schema/traveler_ref.js';

/** Um lugar de retirada ou devolução já resolvido: o ref opaco do voll-places
 *  (`google:<placeId>` | `airport:GRU`), que o agente re-hidrata na busca, e o
 *  rótulo que a pessoa leu. Só os dois prefixos que a locação conhece. */
const RentalPlannerPlaceSchema = z
  .object({
    id: z.string().regex(/^(google|airport):\S+$/),
    label: z.string().trim().min(1).max(200),
  })
  .strict();
export type RentalPlannerPlace = z.infer<typeof RentalPlannerPlaceSchema>;

/** Data-hora LOCAL-NAIVE do balcão (`YYYY-MM-DDTHH:mm`, sem segundos nem fuso):
 *  o formato da barra de busca e dos slots do agente. */
const LocalDateTimeSchema = z.iso.datetime({ local: true, precision: -1 });

const TERM = z.string().trim().min(1).max(80);

/** Por que a busca do cartão não trouxe carros (#5642): nenhum carro no período,
 *  o fornecedor caiu, a retirada cai antes do prazo mínimo da locadora, ou fora
 *  da janela em que as locadoras operam (#7314), ou o período passa do teto da
 *  locadora (1 a 29 dias, #7374). */
export const RentalSearchOutcomeSchema = z.enum([
  'no_results',
  'provider_error',
  'pickup_too_soon',
  'no_vehicles_in_time',
  'rental_too_long',
]);
export type RentalSearchOutcome = z.infer<typeof RentalSearchOutcomeSchema>;

/**
 * O cartão de busca do carro NA CONVERSA (`rental_planner`, #5641): a barra da
 * bancada (`RentalSearchBar`) num card, emitida SEMPRE antes da primeira busca.
 * Vem semeada com o que o agente já sabe — os lugares resolvidos, ou o termo dito
 * que não resolveu num lugar só (o campo abre buscando por ele), e as datas; o
 * Buscar devolve a busca por `interaction` (este challenge + a
 * `RentalPlannerSearch`), com os lugares como ref, nunca texto livre.
 */
export const RentalPlannerBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('rental_planner'),
    challengeId: z.string().min(1),
    /** Quem conversa — o nome do "Você" no QUEM. */
    driverName: z.string().trim().min(1).max(120).optional(),
    /** Id de quem conversa — a linha "Você" do QUEM. Sem ele (blocos antigos) o
     *  condutor é só leitura: dirige quem conversa. */
    requesterId: z.string().min(1).optional(),
    prefill: z
      .object({
        pickup: RentalPlannerPlaceSchema.optional(),
        /** Presente ⇒ o switch "Devolução no mesmo local" nasce desligado. */
        dropoff: RentalPlannerPlaceSchema.optional(),
        pickupTerm: TERM.optional(),
        dropoffTerm: TERM.optional(),
        pickupAt: LocalDateTimeSchema.optional(),
        dropoffAt: LocalDateTimeSchema.optional(),
        /** O condutor que a busca anterior levou, quando não é quem conversa. */
        driver: TravelerRefSchema.optional(),
      })
      .strict()
      .optional(),
    /** O cartão de uma busca que não trouxe carros: nasce recolhido nos botões
     *  de saída, abaixo da fala fixa do agente — "Tentar novamente" refaz a busca
     *  semeada pelo mesmo Buscar, "Mudar datas ou horários" abre a barra ali. */
    outcome: RentalSearchOutcomeSchema.optional(),
  })
  .strict();
export type RentalPlannerBlock = z.infer<typeof RentalPlannerBlockSchema>;

/** A busca do card, ecoada em `confirmation.selections`. Sem `dropoff`, a
 *  devolução é no mesmo lugar da retirada; sem `driver`, dirige quem conversa.
 *  O id do condutor é eco do cliente: o perfil e a política dele saem pela
 *  identidade de quem conversa, que só alcança quem o cliente alcança. */
export const RentalPlannerSearchSchema = z
  .object({
    pickup: RentalPlannerPlaceSchema,
    dropoff: RentalPlannerPlaceSchema.optional(),
    pickupAt: LocalDateTimeSchema,
    dropoffAt: LocalDateTimeSchema,
    driver: TravelerRefSchema.optional(),
  })
  .strict();
export type RentalPlannerSearch = z.infer<typeof RentalPlannerSearchSchema>;

/** Os lugares de retirada e devolução do card — os da bancada, no web-chat
 *  `GET /rental/places`. `searchPlaces` é chamada a cada tecla do campo;
 *  `placeAt` troca a posição do navegador pelo lugar real ("Minha localização"),
 *  `null` quando o ponto não geocodifica — ausente, a linha não aparece. */
export interface RentalPlacesPort {
  searchPlaces: (
    term: string,
  ) => Promise<readonly { ref: string; title: string; subtitle?: string; airport: boolean }[]>;
  placeAt?: (position: {
    latitude: number;
    longitude: number;
  }) => Promise<{ ref: string; title: string } | null>;
}
